import {
  MessageWithRelations,
  MessageType,
  MessageListResponse,
  ConversationSummary,
  ConversationListResponse,
} from '../../apis/messaging/messaging.interfaces';
import { mockInstitute, mockClasses } from './conversationClass.mock';
import { mockUsers } from './conversationParticipants.mock';

// Indian message templates
const messageTemplates = [
  'Namaste! Kya aap aaj class mein aayenge?',
  'Reminder: Kal physics ka test hai.',
  'Please submit your assignment by tomorrow.',
  'Class timing has been changed to 10 AM.',
  'Parent-teacher meeting scheduled for next week.',
  'Sports day practice will start from Monday.',
  'Please bring your science project tomorrow.',
  'Holiday declared for Diwali celebration.',
  'Annual day rehearsal at 3 PM today.',
  'Library books return date extended.',
  'Field trip permission slip required.',
  'Science fair registration open now.',
  'Math Olympiad registration deadline approaching.',
  'Cultural event auditions tomorrow.',
  'Debate competition next week.',
  'Please complete your homework.',
  'Test results are available now.',
  'Parent consent required for trip.',
  'School will remain closed tomorrow.',
  'New books available in library.',
  'Please attend the assembly.',
  'Project submission deadline extended.',
  'Inter-school competition next month.',
  'Please bring your ID card.',
  'Fee payment reminder.',
  'Annual day invitation sent.',
  'Please check your grades.',
  'Workshop registration open.',
  'Please submit your documents.',
  'Class photo session tomorrow.',
  'Please bring your sports kit.',
  'Science lab practical tomorrow.',
  'Please complete the survey.',
  'Parent meeting rescheduled.',
  'Please bring your notebook.',
  'Holiday homework assigned.',
  'Please attend the seminar.',
  'Library membership renewal required.',
  'Please submit your form.',
  'Cultural program rehearsal today.',
  'Please bring your calculator.',
  'Field trip details shared.',
  'Please complete your project.',
  'Test schedule updated.',
  'Please bring your textbook.',
  'Workshop certificate available.',
  'Please submit your report.',
  'Annual day tickets available.',
  'Please bring your uniform.',
  'Science exhibition tomorrow.',
  'Please attend the meeting.',
  'Fee structure updated.',
  'Please bring your materials.',
  'Competition results announced.',
  'Please submit your application.',
  'School trip permission required.',
  'Please bring your permission slip.',
  'Event registration closing soon.',
  'Please complete your registration.',
  'New schedule published.',
];

// Current user in mock mode (matches useMessagingService mock_user)
const MOCK_CURRENT_USER_ID = 'mock_user';

// Other users for DM conversations (exclude current user)
const mockOtherUsers = mockUsers.filter((u) => u._id !== MOCK_CURRENT_USER_ID);

function generateDirectMessage(
  index: number,
  otherUserIndex: number,
  isFromCurrentUser: boolean
): MessageWithRelations {
  const otherUser = mockOtherUsers[otherUserIndex];
  const content = messageTemplates[index % messageTemplates.length];
  const msgId = `dm-${otherUserIndex}-${index}`;
  const createdDate = new Date(Date.now() - (index % 20) * 60 * 60 * 1000);
  const createdStr = createdDate.toISOString();

  return {
    id: msgId,
    _id: msgId,
    content,
    sender_id: isFromCurrentUser ? MOCK_CURRENT_USER_ID : otherUser._id,
    receiver_id: isFromCurrentUser ? otherUser._id : MOCK_CURRENT_USER_ID,
    type: MessageType.DIRECT_MESSAGE,
    institute_ref: 'inst1',
    is_read: index % 3 !== 0,
    created_at: createdStr,
    updated_at: createdStr,
    sender: isFromCurrentUser ? mockUsers[0] : otherUser,
    receiver: isFromCurrentUser ? otherUser : mockUsers[0],
    institute: mockInstitute,
  };
}

function generateBroadcastMessage(index: number, classIndex: number): MessageWithRelations {
  const cls = mockClasses[classIndex];
  const content = messageTemplates[(index + 10) % messageTemplates.length];
  const msgId = `bc-${classIndex}-${index}`;
  const createdDate = new Date(Date.now() - (index % 15) * 60 * 60 * 1000);
  const createdStr = createdDate.toISOString();

  return {
    id: msgId,
    _id: msgId,
    content,
    sender_id: mockUsers[index % mockUsers.length]._id,
    class_id: cls._id,
    section: cls.section,
    section_id: cls.section,
    type: MessageType.BROADCAST,
    institute_ref: 'inst1',
    is_read: true,
    created_at: createdStr,
    updated_at: createdStr,
    sender: mockUsers[index % mockUsers.length],
    class: cls,
    institute: mockInstitute,
  };
}

// Build messages: ~8 DMs per other user + ~6 broadcasts per class
const dmMessages: MessageWithRelations[] = mockOtherUsers.flatMap((_, userIdx) =>
  Array.from({ length: 8 }, (_, i) =>
    generateDirectMessage(i, userIdx, i % 2 === 0)
  )
);
const broadcastMessages: MessageWithRelations[] = mockClasses.flatMap((_, classIdx) =>
  Array.from({ length: 6 }, (_, i) => generateBroadcastMessage(i, classIdx))
);

export const mockMessages: MessageWithRelations[] = [...dmMessages, ...broadcastMessages];

export const mockMessageListResponse: MessageListResponse = {
  data: mockMessages,
  meta: {
    page: 1,
    limit: 20,
    total: mockMessages.length,
    total_pages: Math.ceil(mockMessages.length / 20),
    has_next: mockMessages.length > 20,
    has_previous: false,
  },
};

// One DM conversation per other user, one broadcast per class
function getLastMessageForConversation(
  type: MessageType,
  otherUserIndex?: number,
  classIndex?: number
): MessageWithRelations | undefined {
  if (type === MessageType.DIRECT_MESSAGE && otherUserIndex !== undefined) {
    const convDMs = mockMessages.filter(
      (m) =>
        m.type === MessageType.DIRECT_MESSAGE &&
        (m.receiver_id === mockOtherUsers[otherUserIndex]._id ||
          m.sender_id === mockOtherUsers[otherUserIndex]._id)
    );
    return convDMs.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  }
  if (type === MessageType.BROADCAST && classIndex !== undefined) {
    const cls = mockClasses[classIndex];
    const convBCs = mockMessages.filter(
      (m) => m.type === MessageType.BROADCAST && m.class_id === cls._id
    );
    return convBCs.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  }
  return undefined;
}

const dmConversations: ConversationSummary[] = mockOtherUsers.map((user, idx) => {
  const lastMsg = getLastMessageForConversation(MessageType.DIRECT_MESSAGE, idx);
  return {
    conversation_id: `dm-${user._id}`,
    type: MessageType.DIRECT_MESSAGE,
    other_user: user,
    otherUser: user,
    last_message: lastMsg,
    lastMessage: lastMsg,
    unread_count: idx % 3,
    unreadCount: idx % 3,
    last_activity: lastMsg?.created_at ?? new Date().toISOString(),
  };
});

const broadcastConversations: ConversationSummary[] = mockClasses.map((cls, idx) => {
  const lastMsg = getLastMessageForConversation(MessageType.BROADCAST, undefined, idx);
  return {
    conversation_id: `bc-${cls._id}`,
    type: MessageType.BROADCAST,
    class_info: cls,
    section: cls.section,
    last_message: lastMsg,
    lastMessage: lastMsg,
    unread_count: 0,
    unreadCount: 0,
    last_activity: lastMsg?.created_at ?? new Date().toISOString(),
  };
});

export const mockConversations: ConversationSummary[] = [
  ...dmConversations,
  ...broadcastConversations,
];

export const mockConversationListResponse: ConversationListResponse = {
  data: mockConversations,
  meta: {
    page: 1,
    limit: 20,
    total: mockConversations.length,
    total_pages: Math.ceil(mockConversations.length / 20),
    has_next: mockConversations.length > 20,
    has_previous: false,
  },
};

// ================== TYPING INDICATOR ==================
export const mockTypingIndicator = {
  userId: 'u2',
  conversationId: 'c1',
  isTyping: true,
};
